import React, { useMemo, useState } from "react";
import UIModal from "../UIModal/UIModal";
import { ITrack } from "@src/types/track";
import { ITag } from "@src/types/tags";
import { theme } from "@src/theme";
import styled from "styled-components";
import UpdateTrackTagsModal from "./UpdateTrackTagsModal";
import UpdateTrackRegularTagsModal from "./UpdateTrackRegularTagsModal";
import useModal from "@src/hooks/useModal";
import { useUpdateTrackMutation } from "@src/queries/trackQueries";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { useNotification } from "@src/context/NotificationContext";
import Spinner from "../Spinner";
import EditIcon from "@src/assets/img/edit-icon.svg";
import { useRole } from "@src/hooks/useAuth";

const TagsContainer = styled.div<{ $isLoading?: boolean }>`
  padding: ${theme.margins.small};
  display: flex;
  flex-direction: column;
  gap: ${theme.margins.medium};
  position: relative;
  opacity: ${(props) => (props.$isLoading ? 0.5 : 1)};
  transition: opacity 0.3s ease;
  pointer-events: ${(props) => (props.$isLoading ? "none" : "auto")};
`;

const TagsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.margins.small};
`;

const TagsSectionRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.margins.large};
  flex-wrap: wrap;
`;

const TagsSectionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.margins.small};
  margin-bottom: ${theme.margins.xsmall};
`;

const TagsSectionTitle = styled.div`
  font-size: 0.9rem;
  font-weight: bold;
  color: ${theme.colors.yellow};
`;

const EditIconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  width: 20px;
  height: 20px;

  &:hover {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  img {
    width: 16px;
    height: 16px;
    filter: brightness(0) saturate(100%) invert(85%) sepia(100%) saturate(7499%)
      hue-rotate(358deg) brightness(102%) contrast(101%);
  }
`;

const TagsPillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.margins.small};
`;

const TagPill = styled.div`
  background-color: ${theme.colors.yellow};
  color: #000;
  border: 1px solid ${theme.colors.yellow};
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(255, 204, 0, 0.3);
  }
`;

const EmptyMessage = styled.div`
  color: ${theme.colors.gray};
  font-style: italic;
  padding: ${theme.margins.small} 0;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px dashed ${theme.colors.gray};
  border-radius: 8px;
  padding: ${theme.margins.small};
  text-align: center;

  &:hover {
    color: ${theme.colors.yellow};
    border-color: ${theme.colors.yellow};
    background-color: rgba(255, 204, 0, 0.1);
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const EmotionDataContainer = styled.div`
  padding: ${theme.margins.small} 0;
  border-bottom: 1px solid ${theme.colors.gray300};
  margin-bottom: ${theme.margins.medium};
  display: flex;
  flex-direction: column;
  gap: ${theme.margins.small};
`;

const EmotionDataLine = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.gray};
  line-height: 1.5;
`;

const EmotionDataLabel = styled.strong`
  color: ${theme.colors.primary};
`;

type TrackTagsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  track: ITrack | null;
  onTrackUpdate?: (updatedTrack: ITrack) => void;
  source?: "playlist" | "library" | "untagged";
};

const TrackTagsModal: React.FC<TrackTagsModalProps> = ({
  isOpen,
  onClose,
  track,
  onTrackUpdate,
  source,
}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const updateTrackMutation = useUpdateTrackMutation();
  const role = useRole();
  const canEditTags = role === "admin" || role === "manager";

  // All hooks must be called unconditionally and in the same order every render
  const [genreModalMode, setGenreModalMode] = useState<"genre" | "subGenre">(
    "genre"
  );
  const {
    isOpen: isGenreModalOpen,
    openModal: openGenreModal,
    closeModal: closeGenreModal,
  } = useModal();
  const {
    isOpen: isEmotionModalOpen,
    openModal: openEmotionModal,
    closeModal: closeEmotionModal,
  } = useModal();
  const {
    isOpen: isTagsModalOpen,
    openModal: _openTagsModal,
    closeModal: closeTagsModal,
  } = useModal();

  // Check if playlist tracks or untagged tracks are being fetched
  // All hooks must be called unconditionally to maintain hook order
  const isFetchingPlaylistTracks = useIsFetching({
    queryKey: [QUERY_KEYS.GET_PLAYLIST_TRACKS],
  });
  const isFetchingUntaggedTracks = useIsFetching({
    queryKey: [QUERY_KEYS.UNTAGGED_TRACKS],
  });
  const isFetchingInfiniteTracks = useIsFetching({
    queryKey: [QUERY_KEYS.INFINITE_TRACKS],
  });

  const isFetchingTracks =
    source === "playlist"
      ? isFetchingPlaylistTracks > 0
      : source === "library"
        ? isFetchingInfiniteTracks > 0
        : source === "untagged"
          ? isFetchingUntaggedTracks > 0
          : isFetchingPlaylistTracks > 0 ||
            isFetchingUntaggedTracks > 0 ||
            isFetchingInfiniteTracks > 0;

  const isLoading = updateTrackMutation.isPending || isFetchingTracks;

  const genreTag = useMemo(() => {
    if (!track) return null;
    // Genre is an object, not an array
    if (track.genre && !Array.isArray(track.genre)) {
      return track.genre;
    }
    // Fallback for if it's still an array (backward compatibility)
    if (Array.isArray(track.genre) && track.genre.length > 0) {
      return track.genre[0];
    }
    return null;
  }, [track?.genre]);

  const emotionTag = useMemo(() => {
    if (!track) return null;
    // Emotion is an object, not an array
    if (track.emotion && !Array.isArray(track.emotion)) {
      return track.emotion;
    }
    // Fallback for if it's still an array (backward compatibility)
    if (Array.isArray(track.emotion) && track.emotion.length > 0) {
      return track.emotion[0];
    }
    return null;
  }, [track?.emotion]);

  const genreHasSubGenres = useMemo(() => {
    const raw = genreTag?.subGenres;
    return Array.isArray(raw) && raw.length > 0;
  }, [genreTag?.subGenres]);

  const subGenreTag = useMemo<ITag | null>(() => {
    const rawSubGenre = track?.subGenre;
    if (!rawSubGenre) return null;
    if (typeof rawSubGenre === "string") {
      const trimmed = rawSubGenre.trim();
      return trimmed.length > 0
        ? ({
            documentId: trimmed,
            title: trimmed,
            description: "",
            color: "",
          } as ITag)
        : null;
    }
    if (Array.isArray(rawSubGenre) && rawSubGenre.length > 0) {
      return rawSubGenre[0] ?? null;
    }
    return rawSubGenre as ITag;
  }, [track?.subGenre]);

  const _otherTags = useMemo(() => {
    if (!track) return [];
    if (!track.tags) return [];
    return Array.isArray(track.tags) ? track.tags : [];
  }, [track?.tags, track?.documentId]);

  const handleGenreConfirm = async (
    genreIds?: string[],
    _emotionIds?: string[],
    subGenre?: ITag | null
  ) => {
    if (!track?.documentId) return;

    const genreData =
      genreIds && genreIds.length > 0
        ? { set: genreIds.map((id) => ({ documentId: id })) }
        : { set: [] };
    const subGenreData =
      subGenre?.documentId !== undefined
        ? { set: [{ documentId: subGenre.documentId }] }
        : { set: [] };

    updateTrackMutation.mutate(
      {
        trackId: track.documentId,
        trackData: {
          genre: genreData as any,
          subGenre: subGenreData as any,
        },
      },
      {
        onSuccess: async (response) => {
          const updatedTrack = response.data?.data
            ? {
                ...track,
                ...response.data.data,
                subGenre: response.data?.data?.subGenre ?? subGenre ?? null,
              }
            : {
                ...track,
                genre: response.data?.data?.genre || null,
                subGenre: subGenre ?? null,
              };
          // Invalidate queries to trigger refetch
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_PLAYLIST_TRACKS],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.INFINITE_TRACKS],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.UNTAGGED_TRACKS],
          });

          // Update track immediately, parent will sync with refetched data via useEffect
          if (onTrackUpdate) {
            onTrackUpdate(updatedTrack);
          }
          addNotification("Genre updated successfully", "success");
          closeGenreModal();
        },
        onError: () => {
          addNotification("Failed to update genre", "error");
        },
      }
    );
  };

  const handleEmotionConfirm = async (
    _genreIds?: string[],
    emotionIds?: string[]
  ) => {
    if (!track?.documentId) return;

    const emotionData =
      emotionIds && emotionIds.length > 0
        ? { set: emotionIds.map((id) => ({ documentId: id })) }
        : { set: [] };

    updateTrackMutation.mutate(
      {
        trackId: track.documentId,
        trackData: {
          emotion: emotionData as any,
        },
      },
      {
        onSuccess: async (response) => {
          const updatedTrack = response.data?.data
            ? { ...track, ...response.data.data }
            : { ...track, emotion: response.data?.data?.emotion || null };
          // Invalidate queries to trigger refetch
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_PLAYLIST_TRACKS],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.INFINITE_TRACKS],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.UNTAGGED_TRACKS],
          });

          // Update track immediately, parent will sync with refetched data via useEffect
          if (onTrackUpdate) {
            onTrackUpdate(updatedTrack);
          }
          addNotification("Emotion updated successfully", "success");
          closeEmotionModal();
        },
        onError: () => {
          addNotification("Failed to update emotion", "error");
        },
      }
    );
  };

  const handleTagsConfirm = async (tagIds: string[]) => {
    if (!track?.documentId) return;

    const tagsData =
      tagIds.length > 0
        ? { set: tagIds.map((id) => ({ documentId: id })) }
        : { set: [] };

    // Get current tags to map IDs to tag objects for immediate update
    const currentTags = Array.isArray(track.tags) ? track.tags : [];
    const updatedTagsArray = tagIds
      .map((id) => currentTags.find((tag) => tag.documentId === id))
      .filter((tag): tag is ITag => tag !== undefined);

    // Update track immediately with filtered tags
    const immediatelyUpdatedTrack = { ...track, tags: updatedTagsArray };
    if (onTrackUpdate) {
      onTrackUpdate(immediatelyUpdatedTrack);
    }

    updateTrackMutation.mutate(
      {
        trackId: track.documentId,
        trackData: {
          tags: tagsData as any,
        },
      },
      {
        onSuccess: async (response) => {
          const updatedTrack = response.data?.data
            ? {
                ...track,
                ...response.data.data,
                tags: response.data.data.tags || updatedTagsArray,
              }
            : immediatelyUpdatedTrack;

          // Invalidate queries to trigger refetch
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_PLAYLIST_TRACKS],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.INFINITE_TRACKS],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.UNTAGGED_TRACKS],
          });

          // Update track again with server response if available
          if (onTrackUpdate && response.data?.data?.tags) {
            onTrackUpdate(updatedTrack);
          }
          addNotification("Tags updated successfully", "success");
          closeTagsModal();
        },
        onError: () => {
          // Revert to original track on error
          if (onTrackUpdate) {
            onTrackUpdate(track);
          }
          addNotification("Failed to update tags", "error");
        },
      }
    );
  };

  return (
    <UIModal
      modalStyles={{
        width: 600,
      }}
      onClose={onClose}
      isOpen={isOpen}
      title={track?.title || "Track Tags"}
      actions={[]}
    >
      <TagsContainer $isLoading={isLoading}>
        {/* Emotion Data Section */}
        {track?.emotionData &&
          ((track.emotionData.genres && track.emotionData.genres.length > 0) ||
            (track.emotionData.styles &&
              track.emotionData.styles.length > 0)) && (
            <EmotionDataContainer>
              {track.emotionData.genres &&
                track.emotionData.genres.length > 0 && (
                  <EmotionDataLine>
                    <EmotionDataLabel>Emotion Data - Genres:</EmotionDataLabel>{" "}
                    {track.emotionData.genres.join(", ")}
                  </EmotionDataLine>
                )}
              {track.emotionData.styles &&
                track.emotionData.styles.length > 0 && (
                  <EmotionDataLine>
                    <EmotionDataLabel>Emotion Data - Styles:</EmotionDataLabel>{" "}
                    {track.emotionData.styles.join(", ")}
                  </EmotionDataLine>
                )}
            </EmotionDataContainer>
          )}
        {/* Genre + Sub-genre on same row */}
        <TagsSectionRow>
          <TagsSection style={{ flex: 1, minWidth: 140 }}>
            <TagsSectionTitleContainer>
              <TagsSectionTitle>Genre</TagsSectionTitle>
              {canEditTags && (
                <EditIconButton
                  onClick={() => {
                    setGenreModalMode("genre");
                    openGenreModal();
                  }}
                  disabled={isLoading}
                  title="Edit genre"
                >
                  <img src={EditIcon} alt="edit" />
                </EditIconButton>
              )}
            </TagsSectionTitleContainer>
            {genreTag ? (
              <TagsPillsContainer>
                <TagPill
                  key={genreTag.documentId}
                  onClick={
                    canEditTags
                      ? () => {
                          setGenreModalMode("genre");
                          openGenreModal();
                        }
                      : undefined
                  }
                  style={{
                    cursor: canEditTags ? "pointer" : "default",
                  }}
                >
                  {genreTag.title}
                </TagPill>
              </TagsPillsContainer>
            ) : (
              <EmptyMessage
                onClick={
                  canEditTags
                    ? () => {
                        setGenreModalMode("genre");
                        openGenreModal();
                      }
                    : undefined
                }
                style={{
                  cursor: canEditTags ? "pointer" : "default",
                }}
              >
                {canEditTags ? "No genre tag (click to add)" : "No genre tag"}
              </EmptyMessage>
            )}
          </TagsSection>

          <TagsSection style={{ flex: 1, minWidth: 140 }}>
            <TagsSectionTitleContainer>
              <TagsSectionTitle>Sub-genre</TagsSectionTitle>
              {canEditTags && (
                <EditIconButton
                  onClick={
                    genreTag && genreHasSubGenres
                      ? () => {
                          setGenreModalMode("subGenre");
                          openGenreModal();
                        }
                      : undefined
                  }
                  disabled={isLoading || !genreTag || !genreHasSubGenres}
                  title={
                    !genreTag
                      ? "Select a genre first"
                      : !genreHasSubGenres
                        ? "This genre has no sub-genres"
                        : "Edit sub-genre"
                  }
                >
                  <img src={EditIcon} alt="edit" />
                </EditIconButton>
              )}
            </TagsSectionTitleContainer>
            {subGenreTag ? (
              <TagsPillsContainer>
                <TagPill
                  key={subGenreTag.documentId || subGenreTag.title}
                  onClick={
                    canEditTags && genreTag && genreHasSubGenres
                      ? () => {
                          setGenreModalMode("subGenre");
                          openGenreModal();
                        }
                      : undefined
                  }
                  style={{
                    cursor:
                      canEditTags && genreTag && genreHasSubGenres
                        ? "pointer"
                        : "default",
                  }}
                >
                  {subGenreTag.title}
                </TagPill>
              </TagsPillsContainer>
            ) : (
              <EmptyMessage
                onClick={
                  canEditTags && genreTag && genreHasSubGenres
                    ? () => {
                        setGenreModalMode("subGenre");
                        openGenreModal();
                      }
                    : undefined
                }
                style={{
                  cursor:
                    canEditTags && genreTag && genreHasSubGenres
                      ? "pointer"
                      : "default",
                }}
                title={
                  !genreTag
                    ? "Select a genre first"
                    : !genreHasSubGenres
                      ? "This genre has no sub-genres"
                      : undefined
                }
              >
                {canEditTags && !genreTag
                  ? "No sub-genre (select a genre first)"
                  : canEditTags && !genreHasSubGenres
                    ? "No sub-genre (this genre has no sub-genres)"
                    : canEditTags
                      ? "No sub-genre tag (click to add)"
                      : "No sub-genre tag"}
              </EmptyMessage>
            )}
          </TagsSection>
        </TagsSectionRow>
        {/* Emotion Section */}
        <TagsSection>
          <TagsSectionTitleContainer>
            <TagsSectionTitle>Emotion</TagsSectionTitle>
            {canEditTags && (
              <EditIconButton
                onClick={openEmotionModal}
                disabled={isLoading}
                title="Edit emotion"
              >
                <img src={EditIcon} alt="edit" />
              </EditIconButton>
            )}
          </TagsSectionTitleContainer>
          {emotionTag ? (
            <TagsPillsContainer>
              <TagPill
                key={emotionTag.documentId}
                onClick={canEditTags ? openEmotionModal : undefined}
                style={{
                  cursor: canEditTags ? "pointer" : "default",
                }}
              >
                {emotionTag.title}
              </TagPill>
            </TagsPillsContainer>
          ) : (
            <EmptyMessage
              onClick={canEditTags ? openEmotionModal : undefined}
              style={{
                cursor: canEditTags ? "pointer" : "default",
              }}
            >
              {canEditTags ? "No emotion tag (click to add)" : "No emotion tag"}
            </EmptyMessage>
          )}
        </TagsSection>

        {/* Other Tags Section - commented for now
        <TagsSection>
          <TagsSectionTitleContainer>
            <TagsSectionTitle>Tags</TagsSectionTitle>
            {canEditTags && (
              <EditIconButton
                onClick={_openTagsModal}
                disabled={isLoading}
                title="Edit tags"
              >
                <img src={EditIcon} alt="edit" />
              </EditIconButton>
            )}
          </TagsSectionTitleContainer>
          {_otherTags.length > 0 ? (
            <TagsPillsContainer>
              {_otherTags.map((tag) => (
                <TagPill
                  key={tag.documentId}
                  onClick={canEditTags ? _openTagsModal : undefined}
                  style={{
                    cursor: canEditTags ? "pointer" : "default",
                  }}
                >
                  {tag.title}
                </TagPill>
              ))}
            </TagsPillsContainer>
          ) : (
            <EmptyMessage
              onClick={canEditTags ? _openTagsModal : undefined}
              style={{
                cursor: canEditTags ? "pointer" : "default",
              }}
            >
              {canEditTags ? "No tags (click to add)" : "No tags"}
            </EmptyMessage>
          )}
        </TagsSection>
        */}

        {isLoading && (
          <LoadingOverlay>
            <Spinner small />
          </LoadingOverlay>
        )}
      </TagsContainer>
      <UpdateTrackTagsModal
        isOpen={isGenreModalOpen}
        onClose={closeGenreModal}
        onConfirm={handleGenreConfirm}
        track={track}
        tagType="genre"
        subGenreOnly={genreModalMode === "subGenre"}
        loading={updateTrackMutation.isPending}
      />
      <UpdateTrackTagsModal
        isOpen={isEmotionModalOpen}
        onClose={closeEmotionModal}
        onConfirm={handleEmotionConfirm}
        track={track}
        tagType="emotion"
        loading={updateTrackMutation.isPending}
      />
      <UpdateTrackRegularTagsModal
        isOpen={isTagsModalOpen}
        onClose={closeTagsModal}
        onConfirm={handleTagsConfirm}
        track={track}
        loading={updateTrackMutation.isPending}
      />
    </UIModal>
  );
};

export default TrackTagsModal;
